import './AddToWatchlistModal.css';

import React, { useState, useMemo, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { ReusableModal } from '../modals/ReusableModal';
import { PathReport } from '../types/api/PathReport';
import {
  useAddPathToWatchlistMutation,
  useRemovePathFromWatchlistMutation,
  useCreateWatchlistMutation,
} from '../services/edgeApi';
import { usePathWatchlists } from './usePathWatchlists';
import { pathSpecsEqual } from './pathSpecUtils';
import { useAnalytics } from '../hooks/useAnalytics';
import { AnalyticsEvent } from '../analytics';

interface AddToWatchlistModalProps {
  path: PathReport;
  isOpen: boolean;
  onClose: () => void;
}

export const AddToWatchlistModal: React.FC<AddToWatchlistModalProps> = ({
  path,
  isOpen,
  onClose,
}) => {
  const analytics = useAnalytics();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [globalPathName, setGlobalPathName] = useState('');
  const [creatingWatchlist, setCreatingWatchlist] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMode, setSaveMode] = useState<'update' | 'new'>('update');
  const hasInitialized = useRef(false);

  // Use shared hook for watchlist logic
  const {
    watchlists,
    isMember,
    getRouteMemberships,
    getExactMemberships,
    hasRouteMembership,
    getPathNameFromWatchlist,
    isLoading: loading,
    error,
    refetch,
  } = usePathWatchlists(path, { enabled: isOpen });

  const [addPathToWatchlistMutation] = useAddPathToWatchlistMutation();
  const [removePathFromWatchlistMutation] =
    useRemovePathFromWatchlistMutation();
  const [createWatchlist] = useCreateWatchlistMutation();

  const path_fingerprint: string | undefined = path?.original_path_fingerprint;

  const sourceMembership = useMemo(() => {
    if (path.watchlist_path_record_id == null) return null;
    for (const watchlist of watchlists) {
      const record = getRouteMemberships(watchlist).find(
        (membership: any) => membership.id === path.watchlist_path_record_id,
      );
      if (record) return { watchlistId: watchlist.id, record };
    }
    return null;
  }, [getRouteMemberships, path.watchlist_path_record_id, watchlists]);
  const hasEditedSourceMembership = Boolean(
    sourceMembership &&
      !pathSpecsEqual(sourceMembership.record.path_spec, path.path_spec),
  );

  // Local state for which watchlists should have the path
  const [selectedWatchlists, setSelectedWatchlists] = useState<Set<number>>(
    new Set(),
  );

  // Local state for custom names per watchlist
  const [watchlistNames, setWatchlistNames] = useState<Record<number, string>>(
    {},
  );

  // Initialize selected watchlists and names when modal opens (only once per session)
  useEffect(() => {
    if (!isOpen) {
      hasInitialized.current = false;
      return;
    }

    if (hasInitialized.current) {
      return;
    }

    if (loading || !path_fingerprint) {
      return;
    }

    const initialSelected = new Set<number>();
    const initialNames: Record<number, string> = {};

    watchlists.forEach((w: any) => {
      if (
        isMember(w) ||
        (hasEditedSourceMembership && sourceMembership?.watchlistId === w.id)
      ) {
        initialSelected.add(w.id);
        const existingName = getPathNameFromWatchlist(w);
        if (existingName) {
          initialNames[w.id] = existingName;
        }
      }
    });

    setSelectedWatchlists(initialSelected);
    setWatchlistNames(initialNames);
    setSaveMode('update');
    hasInitialized.current = true;
  }, [
    isOpen,
    loading,
    watchlists,
    path_fingerprint,
    isMember,
    getPathNameFromWatchlist,
    hasEditedSourceMembership,
    sourceMembership,
  ]);

  // Sort: watchlists with the path (alphabetized), then without (alphabetized)
  const filteredWatchlists = useMemo(() => {
    const list = Array.isArray(watchlists) ? watchlists : [];
    let filtered = list;
    if (searchQuery.trim()) {
      filtered = list.filter((w) =>
        w.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }
    // Partition into with-path and without-path
    const withPath = filtered.filter((w) => hasRouteMembership(w));
    const withoutPath = filtered.filter((w) => !hasRouteMembership(w));
    // Sort alphabetically by name
    withPath.sort((a, b) => a.name.localeCompare(b.name));
    withoutPath.sort((a, b) => a.name.localeCompare(b.name));
    return [...withPath, ...withoutPath];
  }, [watchlists, searchQuery, hasRouteMembership]);

  const currentMembershipCount = useMemo(() => {
    if (!Array.isArray(watchlists) || !path_fingerprint) {
      return 0;
    }

    return watchlists.reduce(
      (count: number, watchlist: any) =>
        isMember(watchlist) ? count + 1 : count,
      0,
    );
  }, [watchlists, path_fingerprint, isMember]);

  const disableUpdateButton =
    isSaving ||
    !path_fingerprint ||
    !path.path_spec ||
    loading ||
    (currentMembershipCount === 0 && selectedWatchlists.size === 0);

  const handleToggleWatchlist = (watchlistId: number) => {
    setSelectedWatchlists((prev) => {
      const next = new Set(prev);
      if (next.has(watchlistId)) {
        next.delete(watchlistId);
      } else {
        next.add(watchlistId);
        // When checking a watchlist, populate existing name if it exists
        const watchlist = watchlists.find((w: any) => w.id === watchlistId);
        if (watchlist) {
          const existingName = getPathNameFromWatchlist(watchlist);
          if (existingName) {
            setWatchlistNames((prev) => ({
              ...prev,
              [watchlistId]: existingName,
            }));
          }
        }
      }
      return next;
    });
  };

  const handleWatchlistNameChange = (watchlistId: number, name: string) => {
    setWatchlistNames((prev) => ({
      ...prev,
      [watchlistId]: name,
    }));
  };

  // const handleApplyNameToAll = () => {
  //   if (!globalPathName.trim()) return;

  //   const newNames: Record<number, string> = {};
  //   selectedWatchlists.forEach((id) => {
  //     newNames[id] = globalPathName;
  //   });
  //   setWatchlistNames((prev) => ({ ...prev, ...newNames }));
  // };

  const handleCreateWatchlist = async () => {
    if (!newWatchlistName.trim()) return;

    setCreatingWatchlist(true);
    try {
      const wl = await createWatchlist({ name: newWatchlistName }).unwrap();
      if (wl?.id) {
        // Refetch watchlists to ensure the new one appears in the list
        await refetch();
        // Add to selected watchlists
        setSelectedWatchlists((prev) => new Set(prev).add(wl.id));
        // Pre-fill with global name if available
        if (globalPathName.trim()) {
          setWatchlistNames((prev) => ({
            ...prev,
            [wl.id]: globalPathName,
          }));
        }
      }
      setNewWatchlistName('');
      setShowCreateForm(false);
    } catch (e) {
      console.error('Create watchlist failed:', e);
    } finally {
      setCreatingWatchlist(false);
    }
  };

  const handleSave = async () => {
    const pathSpec = path.path_spec;
    if (!path_fingerprint || !pathSpec) {
      console.warn('Missing path fingerprint or path spec on path:', path);
      return;
    }

    setIsSaving(true);
    try {
      const toRemove = watchlists.flatMap((watchlist: any) =>
        selectedWatchlists.has(watchlist.id)
          ? []
          : getExactMemberships(watchlist).map((membership: any) => ({
              watchlistId: watchlist.id,
              membershipId: membership.id,
            })),
      );
      if (
        sourceMembership &&
        !selectedWatchlists.has(sourceMembership.watchlistId) &&
        !toRemove.some(
          ({ membershipId }) => membershipId === sourceMembership.record.id,
        )
      ) {
        toRemove.push({
          watchlistId: sourceMembership.watchlistId,
          membershipId: sourceMembership.record.id,
        });
      }

      // Saving uses separate requests and can partially succeed. Wait for
      // the whole batch before allowing a retry.
      const removals = await Promise.allSettled(
        toRemove.map(({ watchlistId, membershipId }) =>
          removePathFromWatchlistMutation({
            watchlistId,
            path_fingerprint,
            membership_id: membershipId,
          }).unwrap(),
        ),
      );

      for (const result of removals) {
        if (result.status === 'rejected') throw result.reason;
      }

      const selectedWatchlistsArray = Array.from(selectedWatchlists);

      // Add new memberships and persist the current spec on existing memberships.
      const appends = await Promise.allSettled(
        selectedWatchlistsArray.map((id) =>
          addPathToWatchlistMutation({
            watchlistId: id,
            path_fingerprint,
            name: watchlistNames[id] || undefined,
            path_spec: pathSpec,
            membership_id:
              hasEditedSourceMembership &&
              saveMode === 'update' &&
              sourceMembership?.watchlistId === id
                ? sourceMembership.record.id
                : undefined,
          }).unwrap(),
        ),
      );

      for (const result of appends) {
        if (result.status === 'rejected') throw result.reason;
      }
      if (selectedWatchlistsArray.length > 0) {
        analytics.logEvent(AnalyticsEvent.SAVED_PATH, {
          path_fingerprint,
          selected_watchlists: selectedWatchlistsArray,
          to_remove: toRemove,
        });
      }
      handleClose();
    } catch (e) {
      console.error('Save watchlist changes failed:', e);
      toast.error(
        'Could not save all watchlist changes. Some changes may already be saved. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setShowCreateForm(false);
    setNewWatchlistName('');
    setGlobalPathName('');
    setSelectedWatchlists(new Set());
    setWatchlistNames({});
    setSaveMode('update');
    onClose();
  };

  return (
    <ReusableModal
      isOpen={isOpen}
      onClose={handleClose}
      header="Save Path to Watchlists"
      size="md"
    >
      <div className="watchlist-modal-content d-flex flex-column gap-3">
        {hasEditedSourceMembership && sourceMembership && (
          <fieldset className="border rounded p-3">
            <legend className="float-none w-auto px-1 fs-6 mb-2">
              This saved path has unsaved changes
            </legend>
            <label className="form-check mb-2">
              <input
                type="radio"
                className="form-check-input"
                checked={saveMode === 'update'}
                onChange={() => setSaveMode('update')}
              />
              <span className="form-check-label">
                Update the existing saved version
              </span>
            </label>
            <label className="form-check mb-0">
              <input
                type="radio"
                className="form-check-input"
                checked={saveMode === 'new'}
                onChange={() => setSaveMode('new')}
              />
              <span className="form-check-label">
                Save as a new version and keep the existing one
              </span>
            </label>
          </fieldset>
        )}
        {/* Search bar */}
        <div className="watchlist-search-section pb-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search watchlists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Global path name input with Apply to All button */}
        {/* <div className="watchlist-global-name-section p-3">
          <label className="form-label small text-muted mb-1">
            Path name (optional)
          </label>
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Custom path name for watchlist"
              value={globalPathName}
              onChange={(e) => setGlobalPathName(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={handleApplyNameToAll}
              disabled={!globalPathName.trim() || selectedWatchlists.size === 0}
              title="Apply this name to all selected watchlists"
            >
              Apply to All
            </button>
          </div>
        </div> */}

        {/* Watchlist items */}
        <div className="watchlist-items-section">
          {loading ? (
            <div className="text-center py-4 text-muted">
              Loading watchlists...
            </div>
          ) : error ? (
            <div className="text-center py-4 text-danger">
              Error loading watchlists
            </div>
          ) : filteredWatchlists.length === 0 ? (
            <div className="text-center py-4 text-muted">
              {searchQuery.trim()
                ? 'No matching watchlists'
                : 'No watchlists yet'}
            </div>
          ) : (
            <div className="watchlist-list d-flex flex-column gap-2">
              {filteredWatchlists.map((w) => {
                const id = w.id;
                const isSelected = selectedWatchlists.has(id);
                const disabled = !path_fingerprint;
                const ownershipLabel = w.user == null ? 'Global' : '';
                const exactCount = getExactMemberships(w).length;
                const routeCount = getRouteMemberships(w).length;
                const similarCount = routeCount - exactCount;

                return (
                  <div
                    key={id}
                    className={`watchlist-item-container ${
                      disabled ? 'disabled' : ''
                    }`}
                  >
                    <label
                      className={`watchlist-item-header d-flex align-items-center justify-content-between p-3 ${
                        disabled ? 'disabled' : ''
                      }`}
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={disabled}
                          onChange={() => handleToggleWatchlist(id)}
                          className="watchlist-checkbox me-3"
                        />
                        <span className="watchlist-name">{w.name}</span>
                        {exactCount > 0 ? (
                          <small className="badge bg-success ms-2">
                            Exact saved
                          </small>
                        ) : similarCount > 0 ? (
                          <small className="badge bg-info text-dark ms-2">
                            {similarCount} other{' '}
                            {similarCount === 1 ? 'version' : 'versions'}
                          </small>
                        ) : null}
                      </div>
                      {ownershipLabel ? (
                        <small className="text-muted ms-2">
                          {ownershipLabel}
                        </small>
                      ) : null}
                    </label>
                    {isSelected && (
                      <div className="watchlist-name-input-container px-3 pb-3">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder={`Path name in ${w.name} (optional)`}
                          value={watchlistNames[id] || ''}
                          onChange={(e) =>
                            handleWatchlistNameChange(id, e.target.value)
                          }
                          disabled={disabled}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Create new watchlist section */}
        <div className="watchlist-create-section pt-3">
          {!showCreateForm ? (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm w-100"
              onClick={() => setShowCreateForm(true)}
            >
              + Create New Watchlist
            </button>
          ) : (
            <div className="create-form">
              <input
                type="text"
                className="form-control form-control-sm mb-2"
                placeholder="New watchlist name..."
                value={newWatchlistName}
                onChange={(e) => setNewWatchlistName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateWatchlist();
                  } else if (e.key === 'Escape') {
                    setShowCreateForm(false);
                    setNewWatchlistName('');
                  }
                }}
                autoFocus
              />
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm flex-grow-1"
                  onClick={handleCreateWatchlist}
                  disabled={!newWatchlistName.trim() || creatingWatchlist}
                >
                  {creatingWatchlist ? 'Creating...' : 'Create & Add Path'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewWatchlistName('');
                  }}
                  disabled={creatingWatchlist}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleClose}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={disableUpdateButton}
        >
          {isSaving
            ? 'Saving...'
            : hasEditedSourceMembership && saveMode === 'update'
            ? 'Update saved version'
            : hasEditedSourceMembership && saveMode === 'new'
            ? 'Save as new version'
            : 'Save changes'}
        </button>
      </div>
    </ReusableModal>
  );
};
